/**
 * Prestige Screen - Blockchain + Buffs + Metaverse
 * Skeleton build + patch rendering pattern
 */
import { calculateBlockchainReward, canPrestige2, calculateMetaCoinReward, PRESTIGE_BUFFS, META_UPGRADES } from '../game/config/prestige.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { onRender, getState, dispatch, setInnerHTML, showModal, requestRender, markScreenDirty } from './renderer.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';

let container: HTMLDivElement | null = null;

export function initPrestigeScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  container = document.createElement('div');
  container.id = 'prestige-screen';
  container.className = 'screen-panel';
  content.appendChild(container);

  onRender(renderPrestige);
}

function renderPrestige() {
  if (!container) return;
  const st = getState();

  let html = '';

  // Section 1: Blockchain prestige
  const reward = calculateBlockchainReward(st.statistics.totalEarned);
  html += `<div class="card">
    <div class="card-title card-title-cyan">${t('prestige_blockchain_title')}</div>
    <div class="card-stat">${t('prestige_blockchain_owned')}: <b class="text-cyan">${st.prestige.blockchain}</b> (+${(st.prestige.blockchain * 1).toFixed(0)}%)</div>
    <div class="card-stat-dim">${t('prestige_count')}: ${st.prestige.count}${t('prestige_count_unit')}</div>`;

  if (reward > 0) {
    html += `<div class="card-reward text-green">${t('prestige_reward_prefix')}+${reward} ${t('prestige_blockchain_unit')}</div>
      <button class="act-btn act-btn-cyan" data-action="prestige1">${t('prestige_btn')}</button>`;
  } else {
    html += `<div class="card-stat-red">${t('prestige_need_1m')} (${t('prestige_current')}: ${formatNumber(st.statistics.totalEarned)})</div>`;
  }
  html += `</div>`;

  // Section 2: Blockchain buffs
  html += `<div class="card">
    <div class="card-title card-title-cyan">${t('prestige_buff_title')}</div>`;
  for (const buff of PRESTIGE_BUFFS) {
    const lv = st.prestige.buffs[buff.id] || 0;
    const cost = Math.floor(buff.baseCost * Math.pow(buff.costMultiplier, lv));
    const can = st.prestige.blockchain >= cost && lv < buff.maxLevel;
    const bName = t(buff.id) || buff.name;
    const bDesc = t(buff.id + '_desc') || buff.description;

    html += `<div class="buff-row">
      <div class="buff-info">
        <div class="buff-name">${bName} <span class="text-dim">Lv.${lv}/${buff.maxLevel}</span></div>
        <div class="buff-desc">${bDesc}</div>
      </div>`;
    if (lv < buff.maxLevel) {
      html += `<button class="buff-btn${can ? ' buff-can' : ''}" data-buff="${buff.id}" ${can ? '' : 'disabled'}>${cost}</button>`;
    }
    html += `</div>`;
  }
  html += `</div>`;

  // Section 3: Metaverse (2nd prestige)
  if (st.prestige2.unlocked) {
    html += `<div class="card">
      <div class="card-title card-title-purple">${t('prestige2_title')}</div>
      <div class="card-stat">${t('prestige2_meta_coins')}: <b class="text-purple">${st.prestige2.metaCoins}</b></div>
      <div class="card-stat-dim">${t('prestige2_count')}: ${st.prestige2.count}${t('prestige_count_unit')}</div>`;

    if (canPrestige2(st.prestige.blockchain)) {
      const mcR = calculateMetaCoinReward(st.prestige.blockchain);
      html += `<div class="card-reward text-green">${t('prestige2_reward_prefix')}+${mcR} ${t('prestige2_meta_unit')}</div>
        <button class="act-btn act-btn-purple" data-action="prestige2">${t('prestige2_btn')}</button>`;
    } else {
      html += `<div class="card-stat-red">${t('prestige2_need_100')} (${t('prestige_current')}: ${st.prestige.blockchain})</div>`;
    }

    // Meta upgrades
    html += `<div class="card-subtitle card-title-purple">${t('prestige2_upgrades_title')}</div>`;
    for (const up of META_UPGRADES) {
      const bought = st.prestige2.purchasedUpgrades.includes(up.id);
      const can = !bought && st.prestige2.metaCoins >= up.cost;
      const mName = t('meta_' + up.id) || up.name;
      const mDesc = t('meta_' + up.id + '_desc') || up.description;

      html += `<div class="buff-row${bought ? ' buff-bought' : ''}">
        <div class="buff-info">
          <div class="buff-name">${mName} ${bought ? '&#10003;' : ''}</div>
          <div class="buff-desc">${mDesc}</div>
        </div>`;
      if (!bought) {
        html += `<button class="buff-btn buff-btn-purple${can ? ' buff-can' : ''}" data-meta="${up.id}" ${can ? '' : 'disabled'}>${up.cost}</button>`;
      }
      html += `</div>`;
    }
    html += `</div>`;
  } else {
    html += `<div class="card card-locked">
      <div class="card-title card-title-purple">${t('prestige2_locked_title')}</div>
      <div class="card-stat-dim">${t('prestige2_locked_desc')}</div>
      <div class="card-stat-dim">${t('prestige_current')}: ${st.prestige.count}${t('prestige_count_unit')} / ${st.prestige.blockchain} ${t('prestige_blockchain_unit')}</div>
    </div>`;
  }

  if (setInnerHTML(container, html)) {
    // Bind events
    container.querySelector('[data-action="prestige1"]')?.addEventListener('click', () => confirmPrestige(1));
    container.querySelector('[data-action="prestige2"]')?.addEventListener('click', () => confirmPrestige(2));

    container.querySelectorAll('[data-buff]').forEach(btn => {
      btn.addEventListener('click', () => {
        const buffId = (btn as HTMLElement).dataset.buff!;
        dispatch({ type: 'BUY_PRESTIGE_BUFF', payload: { buffId } });
        playSfx('purchase');
        markScreenDirty();
        requestRender();
      });
    });

    container.querySelectorAll('[data-meta]').forEach(btn => {
      btn.addEventListener('click', () => {
        const upgId = (btn as HTMLElement).dataset.meta!;
        dispatch({ type: 'BUY_META_UPGRADE', payload: { upgradeId: upgId } });
        playSfx('purchase');
        markScreenDirty();
        requestRender();
      });
    });
  }
}

function confirmPrestige(tier: 1 | 2) {
  const st = getState();

  if (tier === 1) {
    const rw = calculateBlockchainReward(st.statistics.totalEarned);
    const currentBonus = (st.prestige.blockchain * 1).toFixed(0);
    const afterBonus = ((st.prestige.blockchain + rw) * 1).toFixed(0);

    showModal(
      t('prestige_confirm_title'),
      `<div class="modal-detail">
        ${t('prestige_confirm_gain')}: <b class="text-cyan">+${rw} ${t('prestige_blockchain_unit')}</b><br>
        ${t('prestige_current')}: +${currentBonus}% &rarr; +${afterBonus}%<br><br>
        ${t('prestige_confirm_warn')}<br>
        ${t('prestige_confirm_keep')}
      </div>`,
      [
        { label: t('btn_cancel'), color: 'var(--card-border)', onClick: () => {} },
        {
          label: t('prestige_btn'), color: 'var(--cyan)', onClick: () => {
            dispatch({ type: 'PRESTIGE' });
            playSfx('prestige');
            markScreenDirty();
            requestRender();
          }
        },
        {
          label: t('prestige_ad_bonus'), color: 'var(--gold)', onClick: async () => {
            const rewards = getAdRewards();
            const result = await AdManager.showRewardedAd(rewards.PRESTIGE_BONUS);
            dispatch({ type: 'PRESTIGE' });
            if (result) {
              const bonusBC = Math.max(1, Math.floor(rw * 1.0));
              dispatch({ type: 'PRESTIGE_AD_BONUS', payload: { bonusBlockchain: bonusBC } });
            }
            playSfx('prestige');
            markScreenDirty();
            requestRender();
          }
        },
      ]
    );
  } else {
    const mcR = calculateMetaCoinReward(st.prestige.blockchain);
    showModal(
      t('prestige2_confirm_title'),
      `<div class="modal-detail">
        ${t('prestige2_confirm_gain')}: <b class="text-purple">+${mcR} ${t('prestige2_meta_unit')}</b><br><br>
        ${t('prestige2_confirm_warn')}<br>
        ${t('prestige2_confirm_keep')}
      </div>`,
      [
        { label: t('btn_cancel'), color: 'var(--card-border)', onClick: () => {} },
        {
          label: t('prestige2_btn'), color: 'var(--purple)', onClick: () => {
            dispatch({ type: 'PRESTIGE_2' });
            playSfx('prestige');
            markScreenDirty();
            requestRender();
          }
        },
      ]
    );
  }
}
